/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { IDict } from "../other/constants";
import SearchResultsHeader from "./SearchResultsHeader";
import SearchResultsCards from "./SearchResultsCards";
import { getUnitSearchUrl, getUrl, parseData, parseNewApiData } from "./helpers";
import ContentLayout from "../layouts/ContentLayout";
import UsedProductBanner from "./UsedProductBanner";
import { toast } from "sonner";
import { useSaleUnitType } from "@/store/clientStore";
import { Locale } from "@/i18n.config";
import { SORT_MAP } from "./constants";

const UsedProductBannerProps = {
  bg: "bg-royal-blue",
  heading: "Found your perfect space and solution? Time to get a quote.",
  subheading:
    "Give us a few details and a representative will contact you in less than an hour with your personalized quote.",
  ctaLabel: "Start your project",
  ctaLink: "/request-quote",
  ctaTheme: "primary" as const,
  heroIcon: "list",
  textColor: "text-white",
};

interface IShowroomSearchResults {
  dict: IDict;
  locale: Locale;
}

export interface IFilters {
  coordinates: string | undefined;
  name: string | undefined;
  distance: string | undefined;
  priceRange: string | undefined;
  unitType: string | undefined;
  unitId: string | undefined;
  allUnits: string | undefined;
}

export interface ICard {
  branchName: string;
  branchSlug: string;
  discountedPrice: number;
  id: string;
  name: string;
  obo: true;
  price: number;
  productType: string;
  slug: string;
  unitNumber: string;
}

interface IUnits {
  cards: ICard[] | [];
  total: number;
  error?: string;
}

const ShowroomSearchResults = ({ dict, locale }: IShowroomSearchResults) => {
  console.log("locale", locale);
  const [units, setUnits] = useState<IUnits>({ cards: [], total: 0, error: "" });
  const searchParams = useSearchParams();
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [sort, setSort] = useState("");
  // const [branches, setBranches] = useState<any>(null);
  const [filters, setFilters] = useState<any>({
    coordinates: searchParams.get("coordinates") || undefined,
    name: searchParams.get("name") || undefined,
    distance: searchParams.get("distance") || "within50miles",
    priceRange: searchParams.get("price-range") || undefined,
    unitType: searchParams.get("unit-type") || undefined,
    unitId: searchParams.get("unit-id") || undefined,
    allUnits: searchParams.get("all-units") || undefined,
  });
  const [activeBtn, setActiveBtn] = useState(filters.unitId ? "unit" : "location");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { unitTypeStore } = useSaleUnitType();
  const resetPage = useRef(false);
  const { push } = useRouter();

  useEffect(() => {
    if (filters) {
      resetPage.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (unitTypeStore) {
      const newFilters = {
        coordinates: undefined,
        name: undefined,
        distance: "within50miles",
        priceRange: undefined,
        unitType: unitTypeStore,
        unitId: undefined,
        allUnits: undefined,
      };
      setFilters(newFilters);
      setCurrentPage(1);

      resetPage.current = false;

      setTimeout(() => {
        fetchShowroom(newFilters);
      }, 1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitTypeStore]);

  useEffect(() => {
    if (sort) {
      setCurrentPage(1);
      setTimeout(() => {}, 1);
      resetPage.current = false;
      void fetchShowroom(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  useEffect(() => {
    void fetchShowroom(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const name = searchParams.get("name");
    const coordinates = searchParams.get("coordinates");
    if (name && coordinates) {
      const [lat, lng] = coordinates.split(",");

      setSelectedPlace({
        fromBrowser: true,
        formatted_address: name,
        coords: {
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        },
      });
    }

    void fetchShowroom(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchShowroom = async (filterData: any) => {
    if (resetPage.current) {
      setCurrentPage(1);
      setTimeout(() => {}, 1);
      resetPage.current = false;
    }
    const target = document.getElementById("usedProductResults");

    if (target) {
      const position = target.getBoundingClientRect();

      window.scrollTo(position.left, position.top + window.scrollY - 150);
    }

    await new Promise((r) => setTimeout(r, 500));

    let payload;

    const { unitId, ...rest } = filterData;

    setLoading(true);

    if (activeBtn === "location") {
      payload = { ...rest };

      const url = await getUrl(payload, sort, /* branches, setBranches,*/ currentPage);

      try {
        const data = await fetch("/api/contentful/sales-showroom", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ addition: url }),
        });

        const res = await data.json();

        if (res.error) {
          toast.error("Error fetching data from Contentful");
          setUnits({ cards: [], total: 0, error: res.error.message });
        } else {
          const _cards = parseData(
            res.items || [],
            res.includes?.Entry || [],
            dict["viewUnit"],
            dict["purchaseInquiry"]
          );
          setUnits({ cards: _cards, total: res.total });
        }
      } catch (error: any) {
        console.log(error);
        setUnits({ cards: [], total: 0, error: error.message });
      } finally {
        setLoading(false);
      }
    } else {
      // we use different API for search by unit umber
      const url = getUnitSearchUrl({
        unitId,
        // locale: locale ? (locale === "en" ? "en-US" : "fr-Ca") : "en-US",
        locale: "en-US",
        currentPage,
        order: sort ? SORT_MAP[sort] : "fields.unitNumber",
      });

      try {
        const data = await fetch("/api/contentful/sales-showroom/new-unit-number ", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ addition: url }),
        });

        const res = await data.json();

        if (res.error || res.errors) {
          toast.error("Error fetching data from Contentful");
          console.log("[ERROR] - ", res.error || res.errors);

          setUnits({ cards: [], total: 0, error: res.error?.message || "Error fetching data from Contentful" });
        } else {
          const _cards = parseNewApiData(res, dict["viewUnit"], dict["purchaseInquiry"]);

          setUnits({ cards: _cards, total: res.data?.total || 0 });
        }
      } catch (error: any) {
        console.log(error);
        setUnits({ cards: [], total: 0, error: error.message });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTagClick = (type: string) => {
    setCurrentPage(1);
    setTimeout(() => {}, 1);
    resetPage.current = false;
    if (type === "sort") {
      setSort("");

      setTimeout(() => {
        fetchShowroom(filters);
      }, 0);
    } else {
      const currentUrlParams = new URLSearchParams(searchParams.toString());
      const tmp = { ...filters };

      if (type === "distance") {
        currentUrlParams.delete("distance");
        currentUrlParams.delete("coordinates");
        currentUrlParams.delete("name");

        tmp.coordinates = undefined;
        tmp.distance = "";
        tmp.name = undefined;
      } else if (type === "priceRange") {
        currentUrlParams.delete("price-range");
        tmp.priceRange = undefined;
      } else if (type === "unitType") {
        currentUrlParams.delete("unit-type");
        tmp.unitType = undefined;
      } else if (type === "unitId") {
        currentUrlParams.delete("unit-id");
        tmp.unitId = undefined;
      }

      setFilters(tmp);

      setTimeout(() => {
        push(`${window.location.pathname}?${currentUrlParams.toString()}`);
        fetchShowroom(tmp);
      }, 0);
    }
  };

  const handleApplyFilters = () => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.forEach((value: any, key: any) => {
      newParams.delete(key);
    });

    if (activeBtn === "unit") {
      newParams.delete("distance");
      newParams.delete("coordinates");
      newParams.delete("name");
      newParams.delete("unit-type");
      newParams.delete("price-range");
      filters.unitId && newParams.set("unit-id", filters.unitId);
    } else {
      // ! collect params
      filters.unitType && newParams.set("unit-type", filters.unitType);
      filters.priceRange && newParams.set("price-range", filters.priceRange);

      // ! selectedPlace and distance are valid filters only if both have value
      if (selectedPlace && filters.distance) {
        let name, coordinates;
        if (selectedPlace.fromBrowser) {
          const lat = selectedPlace.coords?.lat;
          const lng = selectedPlace.coords?.lng;
          name = selectedPlace.formatted_address;
          coordinates = `${lat},${lng}`;
        } else {
          const lat = selectedPlace.geometry?.location?.lat();
          const lng = selectedPlace.geometry?.location?.lng();
          name = selectedPlace.formatted_address;
          coordinates = `${lat},${lng}`;
        }

        newParams.set("distance", filters.distance || "");
        newParams.set("coordinates", coordinates);
        newParams.set("name", name || "");
      }
    }

    push(`${window.location.pathname}?${newParams.toString()}`);

    setTimeout(() => {
      fetchShowroom(filters);
    }, 0);
  };

  return (
    <>
      <SearchResultsHeader
        dict={dict}
        filters={filters}
        setFilters={setFilters}
        activeBtn={activeBtn}
        setActiveBtn={setActiveBtn}
        selectedPlace={selectedPlace}
        setSelectedPlace={setSelectedPlace}
        handleApplyFilters={handleApplyFilters}
        loading={loading}
      />
      <SearchResultsCards
        loading={loading}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        total={units.total}
        dict={dict}
        units={units}
        handleTagClick={handleTagClick}
        sort={sort}
        setSort={setSort}
      />
      <ContentLayout bgColorClass="bg-royal-blue-10">
        <UsedProductBanner {...UsedProductBannerProps} />
      </ContentLayout>
    </>
  );
};

export default ShowroomSearchResults;
